import React, { ReactNode } from "react";
import {
  Box,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { TrustFooter } from "@components/TrustFooter";
import { BankAvatar } from "@components";
import { BankImportHeader } from "./BankImportHeader";
import BankImportBackground from "./background-2.svg?react";

const SCREEN_BREAKPOINT = 720;

const variants = {
  enter: (direction: number) => {
    return {
      opacity: 0,
      x: direction ? "-300px" : "300px",
    };
  },
  in: {
    opacity: 1,
    x: 0,
  },
  out: (direction: number) => ({
    opacity: 0,
    x: direction ? "300px" : "-300px",
  }),
};

const transition = {
  type: "tween" as const,
  ease: "anticipate" as const,
  duration: 0.6,
};

const BackgroundImage = styled("div")`
  position: absolute;
  top: 0;
  left: 50%;
  width: ${SCREEN_BREAKPOINT}px;
  transform: translateX(-50%);
  height: 100%;
  overflow: visible;
  z-index: 0;

  & svg {
    width: 1695px;
    color: ${({ theme }) => theme.palette.wizardHeader.spotBg};
    margin-top: -120%;
    margin-left: 60%;
  }
`;

const Container = styled(Box)(({ theme }) => ({
  paddingTop: `calc(48px + ${theme.spacing(4)})`,
  height: "100dvh",
  zIndex: 1,

  [theme.breakpoints.down(SCREEN_BREAKPOINT)]: {
    paddingTop: `calc(48px + ${theme.spacing(1)})`,
  },
}));

const MainContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  overflowY: "auto",
  msOverflowStyle: "none",
  scrollbarWidth: "none",

  "&::-webkit-scrollbar": {
    display: "none",
  },

  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: theme.spacing(4),

  "& h4": {
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.h4.fontWeight,
    lineHeight: theme.typography.h4.lineHeight,
  },
}));

const ChildrenContainer = styled(Paper)`
  position: relative;
  background-color: ${({ theme }) =>
    theme.bankConnection?.background ?? theme.palette.background.default};
  width: 100%;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2.5}px;
  padding: ${({ theme }) => theme.spacing(8)};
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.down(SCREEN_BREAKPOINT)} {
    padding: ${({ theme }) => theme.spacing(0, 4, 4, 4)};
    margin-top: ${({ theme }) => theme.spacing(4)};
    box-shadow: none;
    min-height: 100%;
    border: none;
    background-color: ${({ theme }) => theme.palette.background.default};

    & > div {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }
`;

const FooterChildrenContainer = styled("div")`
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(6.5, 0)};
`;

export interface Props {
  children: ReactNode;
  title: string;
  backUrl: string;
  bankName?: string;
  bankLogoUrl?: string;
  stepId?: string;
}

export const BankImportLayout = ({
  children,
  title,
  backUrl,
  bankName,
  bankLogoUrl,
  stepId = "default-step",
}: Props) => {
  const theme = useTheme();
  const [direction, setDirection] = React.useState(0);
  const isLessMd = useMediaQuery(theme.breakpoints.down(SCREEN_BREAKPOINT));

  const backgroundColor = isLessMd
    ? theme.palette.background.default
    : (theme.bankConnection?.background ?? theme.palette.background.default);

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundColor,
      }}
    >
      {!isLessMd && (
        <BackgroundImage>
          <BankImportBackground />
        </BackgroundImage>
      )}

      <Container position="relative">
        <BankImportHeader
          backUrl={backUrl}
          title={title}
          bankLogoUrl={bankLogoUrl}
        />
        <MainContainer
          id="main-container"
          onClick={(event: React.MouseEvent) => {
            const button = (event.target as HTMLElement).closest("button");
            if (!button) return;
            const testId = button?.getAttribute("data-testid");

            if (testId === "back-button") {
              setDirection(1);
            } else if (
              testId === "submit-button" ||
              testId === "skip-bank-connection-interface"
            ) {
              setDirection(0);
            }
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={isLessMd ? 0 : 5}
            width="100%"
            flexGrow={1}
            maxWidth={SCREEN_BREAKPOINT}
          >
            {!isLessMd && (
              <Box
                display="flex"
                gap={5}
                alignItems="center"
                justifyContent="center"
              >
                <BankAvatar imgUrl={bankLogoUrl} />
                <Typography variant="h4">{bankName}</Typography>
              </Box>
            )}

            <AnimatePresence initial custom={direction} mode="popLayout">
              <motion.div
                key={stepId}
                initial="enter"
                animate="in"
                exit="out"
                custom={direction}
                variants={variants}
                transition={transition}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ChildrenContainer>{children}</ChildrenContainer>
              </motion.div>
            </AnimatePresence>
          </Box>

          <FooterChildrenContainer>
            <TrustFooter />
          </FooterChildrenContainer>
        </MainContainer>
      </Container>
    </Box>
  );
};
